import {IOption} from "@pg-design/select-module";

import type {ISurveyData, ISurveyFilters, ISurveyOfferDetails, ISurveyPropertyDetails} from "../../types/IBuildSurveyParams";
import {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {surveyCalculatePrice} from "./survey_calculate_price";
import {surveyCalculateRoomsRange} from "./survey_calculate_rooms_range";
import {surveyMergeRegions} from "./survey_merge_regions";

type MergedSurveyState = ISurveyData | Partial<ISurveyData>;

// Form select: 1–5 only ("5+" = 5)
const roomSelectValue = (value: number | undefined) => (value != null && value > 5 ? 5 : value) || "";

const isEmptyObject = (value: object | null | undefined) => {
    if (value == null) {
        return true;
    }
    return Object.keys(value as object).length === 0;
};

const doFiltersOrSurveyExist = (filters: ISurveyFilters | null, survey: ISurveyData | null): boolean => {
    const hasFilters = filters != null && Object.keys(filters).length > 0;
    const hasSurvey = survey != null;

    return hasFilters || hasSurvey;
};

// Combines client survey, listing, offer, and filters into new `regions`, `rooms`, and `price` on top of
// the client’s other profile fields.
const mergeContextIntoSurvey = (
    propertyDetails: ISurveyPropertyDetails | null,
    offerDetails: ISurveyOfferDetails | null,
    filters: ISurveyFilters | null,
    clientSurvey: ISurveyData | null
): MergedSurveyState => {
    const isEmptyDetails = !propertyDetails || Object.keys(propertyDetails).length === 0;
    const isEmptyOffer = !offerDetails || Object.keys(offerDetails).length === 0;
    const isEmptyFilterFields = !filters || Object.keys(filters).length === 0;

    if (clientSurvey && isEmptyDetails && isEmptyOffer && isEmptyFilterFields) {
        return clientSurvey;
    }

    const hasOtherSources = doFiltersOrSurveyExist(filters, clientSurvey);
    const hasClientSurvey = clientSurvey != null;

    const regions = surveyMergeRegions(clientSurvey?.regions, propertyDetails?.offer?.region, offerDetails?.region, filters?.region, hasOtherSources);

    const rooms =
        isEmptyDetails && isEmptyOffer && isEmptyFilterFields && clientSurvey
            ? clientSurvey.rooms
            : surveyCalculateRoomsRange(
                  clientSurvey?.rooms,
                  propertyDetails?.rooms,
                  offerDetails?.roomsMin,
                  offerDetails?.roomsMax,
                  filters?.rooms_0,
                  filters?.rooms_1,
                  hasOtherSources
              );

    const price =
        isEmptyDetails && isEmptyOffer && isEmptyFilterFields && clientSurvey
            ? clientSurvey.price
            : surveyCalculatePrice(clientSurvey?.price, propertyDetails?.price, offerDetails?.priceMax, filters?.price_1, hasClientSurvey);

    return {
        ...(clientSurvey || {}),
        regions,
        rooms,
        price
    };
};

const regionOptionsFromIds = (regionIds: number[], regionOptions: IOption<number>[]): IOption<number>[] => {
    return regionIds
        .map((regionId) => {
            const existingOption = regionOptions.find((opt) => opt.value === regionId);

            if (existingOption) {
                return existingOption;
            }

            return {value: regionId, label: ""};
        })
        .filter(Boolean) as IOption<number>[];
};

const preferencesFromMergedSurvey = (params: {merged: MergedSurveyState; maxBudget: number; regionOptions: IOption<number>[]}): IPreferencesDefaultValues => {
    const {merged, maxBudget, regionOptions} = params;

    const regionIds = merged.regions ?? [];
    const roomRange = merged.rooms ?? {lower: 1, upper: 5, bounds: "[]"};
    const priceUpper = merged.price?.upper ?? null;
    const creditPlan = merged.financing_services ?? false;

    const regionSelector = regionOptionsFromIds(regionIds, regionOptions);
    const budget = priceUpper != null && priceUpper > 0 && priceUpper <= maxBudget ? priceUpper : maxBudget;

    return {
        regionSelector,
        regions: regionSelector,
        budget,
        rooms: {
            lower: roomSelectValue(roomRange.lower),
            upper: roomSelectValue(roomRange.upper)
        },
        creditPlan
    };
};

const preferencesFromClientOnly = (clientInfo: ISurveyData, maxBudget: number, regionOptions: IOption<number>[]) => {
    return preferencesFromMergedSurvey({merged: clientInfo, maxBudget, regionOptions});
};

export interface IBuildSurveyOptions {
    clientInfo?: ISurveyData | null;
    propertyDetails: ISurveyPropertyDetails | null;
    offerDetails: ISurveyOfferDetails | null;
    filters: ISurveyFilters | null;
    maxBudget: number;
    regionOptions: IOption<number>[];
}

/**
 * See the file-level comment. Short version: take survey + page context and return form-ready values.
 * `IPreferencesDefaultValues`.
 */
export const buildSurvey = (options: IBuildSurveyOptions): IPreferencesDefaultValues => {
    const {clientInfo, propertyDetails, offerDetails, filters, maxBudget, regionOptions} = options;

    const isEmptyDetails = isEmptyObject(propertyDetails);
    const isEmptyOffer = isEmptyObject(offerDetails);
    const isEmptyFilters = !filters || Object.keys(filters).length === 0;

    if (clientInfo && isEmptyDetails && isEmptyOffer && isEmptyFilters) {
        return preferencesFromClientOnly(clientInfo, maxBudget, regionOptions);
    }

    const searchFilterFields = isEmptyFilters ? null : filters;
    const merged = mergeContextIntoSurvey(propertyDetails, offerDetails, searchFilterFields, clientInfo ?? null);

    return preferencesFromMergedSurvey({merged, maxBudget, regionOptions});
};
import {IOption} from "@pg-design/select-module";

import type {ISurveyData, ISurveyFilters, ISurveyOfferDetails, ISurveyPropertyDetails} from "../../types/IBuildSurveyParams";
import {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {surveyCalculatePrice} from "./survey_calculate_price";
import {surveyCalculateRoomsRange} from "./survey_calculate_rooms_range";
import {surveyMergeRegions} from "./survey_merge_regions";

type MergedSurveyState = ISurveyData | Partial<ISurveyData>;

// Form select: 1–5 only ("5+" = 5)
const roomSelectValue = (value: number | undefined) => (value != null && value > 5 ? 5 : value) || "";

const isEmptyObject = (value: object | null | undefined) => {
    if (value == null) {
        return true;
    }
    return Object.keys(value as object).length === 0;
};

const doFiltersOrSurveyExist = (filters: ISurveyFilters | null, survey: ISurveyData | null): boolean => {
    const hasFilters = filters != null && Object.keys(filters).length > 0;
    const hasSurvey = survey != null;

    return hasFilters || hasSurvey;
};

// Combines client survey, listing, offer, and filters into new `regions`, `rooms`, and `price` on top of
// the client’s other profile fields.
const mergeContextIntoSurvey = (
    propertyDetails: ISurveyPropertyDetails | null,
    offerDetails: ISurveyOfferDetails | null,
    filters: ISurveyFilters | null,
    clientSurvey: ISurveyData | null
): MergedSurveyState => {
    const isEmptyDetails = !propertyDetails || Object.keys(propertyDetails).length === 0;
    const isEmptyOffer = !offerDetails || Object.keys(offerDetails).length === 0;
    const isEmptyFilterFields = !filters || Object.keys(filters).length === 0;

    if (clientSurvey && isEmptyDetails && isEmptyOffer && isEmptyFilterFields) {
        return clientSurvey;
    }

    const hasOtherSources = doFiltersOrSurveyExist(filters, clientSurvey);
    const hasClientSurvey = clientSurvey != null;

    const regions = surveyMergeRegions(clientSurvey?.regions, propertyDetails?.offer?.region, offerDetails?.region, filters?.region, hasOtherSources);

    const rooms =
        isEmptyDetails && isEmptyOffer && isEmptyFilterFields && clientSurvey
            ? clientSurvey.rooms
            : surveyCalculateRoomsRange(
                  clientSurvey?.rooms,
                  propertyDetails?.rooms,
                  offerDetails?.roomsMin,
                  offerDetails?.roomsMax,
                  filters?.rooms_0,
                  filters?.rooms_1,
                  hasOtherSources
              );

    const price =
        isEmptyDetails && isEmptyOffer && isEmptyFilterFields && clientSurvey
            ? clientSurvey.price
            : surveyCalculatePrice(clientSurvey?.price, propertyDetails?.price, offerDetails?.priceMax, filters?.price_1, hasClientSurvey);

    return {
        ...(clientSurvey || {}),
        regions,
        rooms,
        price
    };
};

const regionOptionsFromIds = (regionIds: number[], regionOptions: IOption<number>[]): IOption<number>[] => {
    return regionIds
        .map((regionId) => {
            const existingOption = regionOptions.find((opt) => opt.value === regionId);

            if (existingOption) {
                return existingOption;
            }

            return {value: regionId, label: ""};
        })
        .filter(Boolean) as IOption<number>[];
};

const preferencesFromMergedSurvey = (params: {merged: MergedSurveyState; maxBudget: number; regionOptions: IOption<number>[]}): IPreferencesDefaultValues => {
    const {merged, maxBudget, regionOptions} = params;

    const regionIds = merged.regions ?? [];
    const roomRange = merged.rooms ?? {lower: 1, upper: 5, bounds: "[]"};
    const priceUpper = merged.price?.upper ?? null;
    const creditPlan = merged.financing_services ?? false;

    const regionSelector = regionOptionsFromIds(regionIds, regionOptions);
    const budget = priceUpper != null && priceUpper > 0 && priceUpper <= maxBudget ? priceUpper : maxBudget;

    return {
        regionSelector,
        regions: regionSelector,
        budget,
        rooms: {
            lower: roomSelectValue(roomRange.lower),
            upper: roomSelectValue(roomRange.upper)
        },
        creditPlan
    };
};

const preferencesFromClientOnly = (clientInfo: ISurveyData, maxBudget: number, regionOptions: IOption<number>[]) => {
    return preferencesFromMergedSurvey({merged: clientInfo, maxBudget, regionOptions});
};

export interface IBuildSurveyOptions {
    clientInfo?: ISurveyData | null;
    propertyDetails: ISurveyPropertyDetails | null;
    offerDetails: ISurveyOfferDetails | null;
    filters: ISurveyFilters | null;
    maxBudget: number;
    regionOptions: IOption<number>[];
}

/**
 * See the file-level comment. Short version: take survey + page context and return form-ready values.
 * `IPreferencesDefaultValues`.
 */
export const buildSurvey = (options: IBuildSurveyOptions): IPreferencesDefaultValues => {
    const {clientInfo, propertyDetails, offerDetails, filters, maxBudget, regionOptions} = options;

    const isEmptyDetails = isEmptyObject(propertyDetails);
    const isEmptyOffer = isEmptyObject(offerDetails);
    const isEmptyFilters = !filters || Object.keys(filters).length === 0;

    if (clientInfo && isEmptyDetails && isEmptyOffer && isEmptyFilters) {
        return preferencesFromClientOnly(clientInfo, maxBudget, regionOptions);
    }

    const searchFilterFields = isEmptyFilters ? null : filters;
    const merged = mergeContextIntoSurvey(propertyDetails, offerDetails, searchFilterFields, clientInfo ?? null);

    return preferencesFromMergedSurvey({merged, maxBudget, regionOptions});
};
